import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import {
  useAcknowledgeBackgroundJobMutation,
  useCancelBackgroundJobMutation,
  usePendingBackgroundJobsForUserQuery,
} from 'src/fetch/backgroundJobs/backgroundJobs'
import { BackgroundJobStatusValue } from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { hasBackgroundJobPillNavigationContext } from 'src/fetch/backgroundJobs/backgroundJobPillNavigation'
import { useIsGov, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey } from 'src/utils/queryClient'

import {
  cancelScheduledBackgroundJobAutoAck,
  clearAllScheduledBackgroundJobAutoAcks,
  scheduleBackgroundJobAutoAck,
} from './attemptBackgroundJobAutoAck'
import { shouldAutoDismissTerminalBackgroundJob } from './backgroundJobMessages'
import { buildTrackedJobsFromPendingRows } from './trackedBackgroundJob'
import { RegisterTriggeredBackgroundJobArgs, TrackedBackgroundJob } from './types'

type TerminalSideEffectsJob = Pick<TrackedBackgroundJob, 'id' | 'isInTerminalStatus' | 'status'> & {
  recordTypeId?: number | null
  viewId?: number | null
}

type BackgroundJobsContextValue = {
  cancelJob: (jobId: number) => Promise<void>
  /** Acknowledges a terminal job so it drops out of pendingBackgroundJobsForUser. */
  dismissJob: (jobId: number) => Promise<void>
  /** Session-only view uuid captured at job registration for pill navigation. */
  getPillNavigationViewUuid: (jobId: number) => string | undefined
  /** Refreshes pending jobs after initiateBulkJob and optionally registers terminal onComplete. */
  registerTriggeredBackgroundJob: (args: RegisterTriggeredBackgroundJobArgs) => void
  trackedJobs: TrackedBackgroundJob[]
}

const BackgroundJobsContext = createContext<BackgroundJobsContextValue | null>(null)

type Props = {
  children?: React.ReactNode
}

const BackgroundJobsProvider = ({ children }: Props) => {
  const isGov = useIsGov()
  const activeOrgId = useMyOrgId()
  const { notification } = useNotification()
  const reactQueryClient = useQueryClient()
  const cancelBackgroundJobMutation = useCancelBackgroundJobMutation()
  const acknowledgeBackgroundJobMutation = useAcknowledgeBackgroundJobMutation()
  const prevTerminalStatusRef = useRef(new Map<number, boolean>())
  // Session-only — callbacks are not restored after a full page reload.
  const onCompleteByJobIdRef = useRef(
    new Map<number, NonNullable<RegisterTriggeredBackgroundJobArgs['onComplete']>>()
  )
  const viewUuidByJobIdRef = useRef(new Map<number, string>())
  const onCompletePipelineFinishedJobIdsRef = useRef(new Set<number>())
  const autoAckAttemptedJobIdsRef = useRef(new Set<number>())
  const autoAckTimerByJobIdRef = useRef(new Map<number, ReturnType<typeof setTimeout>>())
  const onCompleteRetriedJobIdsRef = useRef(new Set<number>())
  const onCompleteExhaustedJobIdsRef = useRef(new Set<number>())
  /** Bumped on org switch and provider unmount so in-flight onComplete runs bail out. */
  const onCompleteStaleGuardRef = useRef(0)
  const [onCompleteRegistrationEpoch, setOnCompleteRegistrationEpoch] = useState(0)

  const { data: pendingBackgroundJobsData } = usePendingBackgroundJobsForUserQuery({
    organizationId: activeOrgId,
    enabled: isGov,
  })

  const invalidatePendingBackgroundJobs = useCallback(() => {
    void reactQueryClient.invalidateQueries({
      queryKey: [QueryKey.PendingBackgroundJobsForUser, activeOrgId],
    })
  }, [activeOrgId, reactQueryClient])

  const acknowledgeBackgroundJob = useCallback(
    async (backgroundJobId: number) => {
      await acknowledgeBackgroundJobMutation.mutateAsync({
        input: { id: backgroundJobId },
      })
      invalidatePendingBackgroundJobs()
    },
    [acknowledgeBackgroundJobMutation, invalidatePendingBackgroundJobs]
  )

  const dismissJob = useCallback(
    async (jobId: number) => {
      cancelScheduledBackgroundJobAutoAck(jobId, autoAckTimerByJobIdRef.current)
      autoAckAttemptedJobIdsRef.current.add(jobId)
      try {
        await acknowledgeBackgroundJob(jobId)
      } catch (error) {
        autoAckAttemptedJobIdsRef.current.delete(jobId)
        throw error
      }
    },
    [acknowledgeBackgroundJob]
  )

  const getPillNavigationViewUuid = useCallback((jobId: number) => {
    return viewUuidByJobIdRef.current.get(jobId)
  }, [])

  const registerTriggeredBackgroundJob = useCallback(
    ({ jobId, onComplete, viewUuid }: RegisterTriggeredBackgroundJobArgs) => {
      prevTerminalStatusRef.current.delete(jobId)
      onCompletePipelineFinishedJobIdsRef.current.delete(jobId)
      cancelScheduledBackgroundJobAutoAck(jobId, autoAckTimerByJobIdRef.current)
      autoAckAttemptedJobIdsRef.current.delete(jobId)
      onCompleteRetriedJobIdsRef.current.delete(jobId)
      onCompleteExhaustedJobIdsRef.current.delete(jobId)
      if (onComplete) {
        onCompleteByJobIdRef.current.set(jobId, onComplete)
        setOnCompleteRegistrationEpoch((epoch) => epoch + 1)
      } else {
        onCompleteByJobIdRef.current.delete(jobId)
      }
      if (viewUuid) {
        viewUuidByJobIdRef.current.set(jobId, viewUuid)
      } else {
        viewUuidByJobIdRef.current.delete(jobId)
      }
      invalidatePendingBackgroundJobs()
    },
    [invalidatePendingBackgroundJobs]
  )

  const cancelJob = useCallback(
    async (jobId: number) => {
      await cancelBackgroundJobMutation.mutateAsync({ id: jobId })
      invalidatePendingBackgroundJobs()
    },
    [cancelBackgroundJobMutation, invalidatePendingBackgroundJobs]
  )

  const trackedJobs = useMemo(() => {
    if (!isGov) {
      return []
    }

    const pendingBackgroundJobs = pendingBackgroundJobsData?.pendingBackgroundJobsForUser ?? []
    return buildTrackedJobsFromPendingRows(pendingBackgroundJobs)
  }, [isGov, pendingBackgroundJobsData?.pendingBackgroundJobsForUser])

  useEffect(() => {
    onCompleteStaleGuardRef.current += 1
    prevTerminalStatusRef.current = new Map()
    onCompleteByJobIdRef.current = new Map()
    viewUuidByJobIdRef.current = new Map()
    onCompletePipelineFinishedJobIdsRef.current = new Set()
    autoAckAttemptedJobIdsRef.current = new Set()
    clearAllScheduledBackgroundJobAutoAcks(autoAckTimerByJobIdRef.current)
    onCompleteRetriedJobIdsRef.current = new Set()
    onCompleteExhaustedJobIdsRef.current = new Set()
    setOnCompleteRegistrationEpoch(0)
  }, [activeOrgId])

  useEffect(() => {
    return () => {
      onCompleteStaleGuardRef.current += 1
      clearAllScheduledBackgroundJobAutoAcks(autoAckTimerByJobIdRef.current)
    }
  }, [])

  const pendingBackgroundJobs = useMemo(
    () => (isGov ? (pendingBackgroundJobsData?.pendingBackgroundJobsForUser ?? []) : []),
    [isGov, pendingBackgroundJobsData?.pendingBackgroundJobsForUser]
  )

  useEffect(() => {
    const tryAutoAckTerminalJob = (terminalSideEffectsJob: TerminalSideEffectsJob) => {
      if (!shouldAutoDismissTerminalBackgroundJob(terminalSideEffectsJob)) {
        return
      }

      const { id: backgroundJobId } = terminalSideEffectsJob
      // All trackable completed pills skip the 60s auto-ack (row has viewId + recordTypeId),
      // not only when session viewUuid was captured — keeps the pill after refresh until
      // dismiss/navigate. Rows without that context still auto-ack (no pill to show).
      if (
        terminalSideEffectsJob.status === BackgroundJobStatusValue.Completed &&
        (viewUuidByJobIdRef.current.has(backgroundJobId) ||
          hasBackgroundJobPillNavigationContext(terminalSideEffectsJob))
      ) {
        return
      }

      const hasOnComplete = onCompleteByJobIdRef.current.has(backgroundJobId)
      if (hasOnComplete && !onCompletePipelineFinishedJobIdsRef.current.has(backgroundJobId)) {
        return
      }

      scheduleBackgroundJobAutoAck({
        backgroundJobId,
        autoAckAttemptedJobIds: autoAckAttemptedJobIdsRef.current,
        autoAckTimerByJobId: autoAckTimerByJobIdRef.current,
        acknowledgeBackgroundJob,
      })
    }

    for (const pendingBackgroundJob of pendingBackgroundJobs) {
      const backgroundJobId = pendingBackgroundJob.id
      const isInTerminalStatus = pendingBackgroundJob.isInTerminalStatus ?? false
      const terminalSideEffectsJob: TerminalSideEffectsJob = {
        id: backgroundJobId,
        isInTerminalStatus,
        recordTypeId: pendingBackgroundJob.recordTypeId,
        status: pendingBackgroundJob.status,
        viewId: pendingBackgroundJob.viewId,
      }
      const wasTerminal = prevTerminalStatusRef.current.get(backgroundJobId)
      const justBecameTerminal = isInTerminalStatus && !wasTerminal

      if (justBecameTerminal) {
        const onComplete = onCompleteByJobIdRef.current.get(backgroundJobId)

        if (onComplete) {
          const runId = onCompleteStaleGuardRef.current
          const isStaleOnCompleteRun = () => runId !== onCompleteStaleGuardRef.current

          void (async () => {
            try {
              await onComplete()
              if (isStaleOnCompleteRun()) {
                return
              }
              onCompleteRetriedJobIdsRef.current.delete(backgroundJobId)
              onCompleteExhaustedJobIdsRef.current.delete(backgroundJobId)
            } catch (error) {
              if (isStaleOnCompleteRun()) {
                return
              }
              console.error(error)
              notification.error({
                message:
                  'Could not refresh data after the background job finished. Please refresh the page.',
              })
              if (!onCompleteRetriedJobIdsRef.current.has(backgroundJobId)) {
                onCompleteRetriedJobIdsRef.current.add(backgroundJobId)
                try {
                  await onComplete()
                  if (isStaleOnCompleteRun()) {
                    return
                  }
                  onCompleteExhaustedJobIdsRef.current.delete(backgroundJobId)
                } catch (retryError) {
                  if (isStaleOnCompleteRun()) {
                    return
                  }
                  console.error(retryError)
                  onCompleteExhaustedJobIdsRef.current.add(backgroundJobId)
                }
              } else {
                onCompleteExhaustedJobIdsRef.current.add(backgroundJobId)
              }
            } finally {
              if (!isStaleOnCompleteRun()) {
                onCompletePipelineFinishedJobIdsRef.current.add(backgroundJobId)
                tryAutoAckTerminalJob(terminalSideEffectsJob)
              }
            }
          })()
        } else {
          onCompletePipelineFinishedJobIdsRef.current.add(backgroundJobId)
          tryAutoAckTerminalJob(terminalSideEffectsJob)
        }
      } else if (isInTerminalStatus) {
        tryAutoAckTerminalJob(terminalSideEffectsJob)
      }

      prevTerminalStatusRef.current.set(backgroundJobId, isInTerminalStatus)
    }
  }, [acknowledgeBackgroundJob, notification, onCompleteRegistrationEpoch, pendingBackgroundJobs])

  const value = useMemo(
    () => ({
      cancelJob,
      dismissJob,
      getPillNavigationViewUuid,
      registerTriggeredBackgroundJob,
      trackedJobs,
    }),
    [cancelJob, dismissJob, getPillNavigationViewUuid, registerTriggeredBackgroundJob, trackedJobs]
  )

  return <BackgroundJobsContext.Provider value={value}>{children}</BackgroundJobsContext.Provider>
}

export default React.memo(BackgroundJobsProvider)

export const useBackgroundJobs = (): BackgroundJobsContextValue => {
  const context = useContext(BackgroundJobsContext)
  if (!context) {
    throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider')
  }
  return context
}
